<template>
    <div class="order-details-container">
        <div v-if="order" class="modern-order-card">
            <!-- Order Header -->
            <div class="order-header">
                <div class="header-left">
                    <h3 class="order-title">Order #{{ order.invoice_id }}</h3>
                    <div class="order-meta">
                        <span class="meta-item">
                            <b-icon icon="calendar" class="mr-1"></b-icon>
                            {{ formatDate(order.ts) }}
                        </span>
                        <span class="meta-item">
                            <b-icon icon="geo-alt" class="mr-1"></b-icon>
                            {{ getLocationName(order.location_id) }}
                        </span>
                        <span class="meta-item">
                            <b-icon icon="person" class="mr-1"></b-icon>
                            {{ order.username || 'N/A' }}
                        </span>
                    </div>
                </div>
                
                <div class="header-right">
                    <div class="order-status">
                        <span class="status-badge" :class="getStatusClass(order)">
                            {{ getStatusText(order) }}
                        </span>
                        <span class="payment-badge ml-2" :class="order.is_paid ? 'badge-success' : 'badge-warning'">
                            {{ order.is_paid ? 'Paid' : 'Pending Payment' }}
                        </span>
                    </div>
                    <div class="order-total">
                        <div class="total-label">Total Amount</div>
                        <div class="total-value">{{ formatCurrency(order.total) }}</div>
                    </div>
                </div>
            </div>

            <!-- Customer Information -->
            <b-card class="customer-card mb-4">
                <b-card-header class="bg-light">
                    <h6 class="mb-0">Customer Information</h6>
                </b-card-header>
                <b-card-body>
                    <b-row>
                        <b-col md="4">
                            <div class="customer-info">
                                <label>Name</label>
                                <p>{{ getCustomerField('fullname') }}</p>
                            </div>
                        </b-col>
                        <b-col md="4">
                            <div class="customer-info">
                                <label>Email</label>
                                <p>{{ getCustomerField('email') }}</p>
                            </div>
                        </b-col>
                        <b-col md="4">
                            <div class="customer-info">
                                <label>Phone</label>
                                <p>{{ formatPhone(getCustomerField('phone')) }}</p>
                            </div>
                        </b-col>
                    </b-row>
                    <b-row v-if="hasCustomerAddress()">
                        <b-col md="12">
                            <div class="customer-info">
                                <label>Address</label>
                                <p>
                                    {{ getFullAddress() }}
                                </p>
                            </div>
                        </b-col>
                    </b-row>
                </b-card-body>
            </b-card>

            <!-- Order Items -->
            <b-card class="items-card mb-4">
                <b-card-header class="bg-light">
                    <h6 class="mb-0">Order Items</h6>
                </b-card-header>
                <b-card-body>
                    <div v-if="loadingItems" class="text-center">
                        <div class="spinner-border spinner-border-sm" role="status">
                            <span class="sr-only">Loading...</span>
                        </div>
                        <span class="ml-2">Loading items...</span>
                    </div>
                    
                    <div v-else-if="orderItems.length > 0">
                        <div class="table-responsive">
                            <table class="table table-sm">
                                <thead>
                                    <tr>
                                        <th>Product</th>
                                        <th>SKU</th>
                                        <th class="text-center">Quantity</th>
                                        <th class="text-right">Price</th>
                                        <th class="text-right">Total</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-for="(item, idx) in orderItems" :key="idx">
                                        <td>{{ item.product_name || 'Product ' + (idx + 1) }}</td>
                                        <td>{{ item.sku || 'N/A' }}</td>
                                        <td class="text-center">{{ item.quantity }}</td>
                                        <td class="text-right">{{ formatCurrency(item.selling_price) }}</td>
                                        <td class="text-right">{{ formatCurrency(item.quantity * item.selling_price) }}</td>
                                    </tr>
                                </tbody>
                                <tfoot>
                                    <tr>
                                        <td colspan="4" class="text-right"><strong>Subtotal:</strong></td>
                                        <td class="text-right">{{ formatCurrency(order.subtotal || 0) }}</td>
                                    </tr>
                                    <tr v-if="(order.tax_amount || 0) > 0">
                                        <td colspan="4" class="text-right"><strong>Tax:</strong></td>
                                        <td class="text-right">{{ formatCurrency(order.tax_amount || 0) }}</td>
                                    </tr>
                                    <tr v-if="(order.delivery_charges || 0) > 0">
                                        <td colspan="4" class="text-right"><strong>Delivery:</strong></td>
                                        <td class="text-right">{{ formatCurrency(order.delivery_charges || 0) }}</td>
                                    </tr>
                                    <tr v-if="(order.setup_charges || 0) > 0">
                                        <td colspan="4" class="text-right"><strong>Setup:</strong></td>
                                        <td class="text-right">{{ formatCurrency(order.setup_charges || 0) }}</td>
                                    </tr>
                                    <tr v-if="(order.warranty_charges || 0) > 0">
                                        <td colspan="4" class="text-right"><strong>Warranty:</strong></td>
                                        <td class="text-right">{{ formatCurrency(order.warranty_charges || 0) }}</td>
                                    </tr>
                                    <tr>
                                        <td colspan="4" class="text-right"><strong>Total:</strong></td>
                                        <td class="text-right"><strong>{{ formatCurrency(order.total || 0) }}</strong></td>
                                    </tr>
                                </tfoot>
                            </table>
                        </div>
                    </div>
                    
                    <div v-else class="text-center text-muted">
                        <p>No items found for this order.</p>
                    </div>
                </b-card-body>
            </b-card>

            <!-- Payment Information -->
            <b-card v-if="hasPaymentMethods()" class="payment-card mb-4">
                <b-card-header class="bg-light">
                    <h6 class="mb-0">Payment Information</h6>
                </b-card-header>
                <b-card-body>
                    <div class="table-responsive">
                        <table class="table table-sm">
                            <thead>
                                <tr>
                                    <th>Method</th>
                                    <th>Reference</th>
                                    <th class="text-right">Amount</th>
                                    <th>Status</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="(payment, idx) in getPaymentMethods()" :key="idx">
                                    <td>{{ payment.value || 'N/A' }}</td>
                                    <td>{{ payment.reference || 'N/A' }}</td>
                                    <td class="text-right">{{ formatCurrency(payment.amount || 0) }}</td>
                                    <td>
                                        <span class="badge" :class="getPaymentStatusClass(payment)">
                                            {{ getPaymentStatusText(payment) }}
                                        </span>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </b-card-body>
            </b-card>

            <!-- Additional Information -->
            <b-card v-if="order.notes" class="notes-card">
                <b-card-header class="bg-light">
                    <h6 class="mb-0">Notes</h6>
                </b-card-header>
                <b-card-body>
                    <p class="mb-0">{{ order.notes }}</p>
                </b-card-body>
            </b-card>
            
            <!-- Close Button -->
            <div class="text-center mt-4">
                <b-button variant="outline-primary" @click="closeModal">
                    <b-icon icon="x" class="mr-1"></b-icon>
                    Close
                </b-button>
            </div>
        </div>
        
        <div v-else class="text-center text-muted p-4">
            <div class="spinner-border spinner-border-sm" role="status">
                <span class="sr-only">Loading...</span>
            </div>
            <p class="mt-2">Loading order details...</p>
        </div>
    </div>
</template>

<script>
    import Requestor from '@/utilities/Requestor'

    export default {
        name: "OrderDetails",
        props: ["order"],
        data: function() {
            return {
                packet: {},
                orderItems: [],
                loadingItems: false
            };
        },
        methods: {
            loadOrderItems() {
                if (!this.order || !this.order.invoice_id) {
                    console.error('No order or invoice_id found');
                    return;
                }
                
                console.log('Loading order items for invoice:', this.order.invoice_id);
                this.loadingItems = true;
                var that = this;
                var path = "/api/customers/get_order_items/";
                var data = {
                    invoice_id: this.order.invoice_id
                };
                
                var success = function(response) {
                    console.log('Order items response:', response);
                    if (response.data.error) {
                        console.error('Error loading order items:', response.data.error);
                        that.$bvToast.toast(
                            "Error loading order items",
                            { title: "Error", autoHideDelay: 1500, variant: "warning" }
                        );
                    } else {
                        that.orderItems = response.data.result || [];
                        console.log('Order items loaded:', that.orderItems.length);
                    }
                    that.loadingItems = false;
                };
                
                var failure = function(error) {
                    console.error('Error loading order items:', error);
                    that.$bvToast.toast(
                        "Error loading order items. Please try again.",
                        { title: "Error", autoHideDelay: 1500, variant: "warning" }
                    );
                    that.loadingItems = false;
                };
                
                Requestor.postSyncP(path, data, this, success, failure);
            },
            
            getCustomerField(field) {
                if (!this.order) return 'N/A';
                
                if (this.order.customer && this.order.customer[field]) {
                    return this.order.customer[field];
                }
                
                const fieldMap = {
                    'fullname': this.order.customer_name || 'N/A',
                    'email': this.order.customer_email || 'N/A',
                    'phone': this.order.customer_phone || 'N/A'
                };
                
                return fieldMap[field] || 'N/A';
            },
            
            hasCustomerAddress() {
                if (!this.order || !this.order.customer) return false;
                const customer = this.order.customer;
                return customer.address_line_1 || 
                       customer.address_line_2 || 
                       customer.city || 
                       customer.state || 
                       customer.zipcode;
            },
            
            getFullAddress() {
                if (!this.order || !this.order.customer) return 'N/A';
                const customer = this.order.customer;
                const parts = [
                    customer.address_line_1,
                    customer.address_line_2,
                    customer.city,
                    customer.state,
                    customer.zipcode
                ].filter(Boolean);
                return parts.join(', ') || 'N/A';
            },
            
            hasPaymentMethods() {
                if (!this.order || !this.order.payment_methods) return false;
                return this.order.payment_methods.length > 0;
            },
            
            getPaymentMethods() {
                if (!this.order || !this.order.payment_methods) return [];
                return this.order.payment_methods;
            },
            
            getPaymentStatusClass(payment) {
                if (payment.status === 'completed') return 'badge-success';
                return 'badge-warning';
            },
            
            getPaymentStatusText(payment) {
                return payment.status || 'Pending';
            },
            
            formatCurrency(value) {
                if (!value && value !== 0) return '$0.00';
                const num = parseFloat(value);
                if (isNaN(num)) return '$0.00';
                return '$' + num.toLocaleString('en-US', {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2
                });
            },
            
            formatDate(dateString) {
                if (!dateString) return 'N/A';
                try {
                    var date = new Date(dateString);
                    return date.toLocaleDateString('en-US', {
                        month: 'short',
                        day: 'numeric',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                    });
                } catch (error) {
                    return dateString;
                }
            },
            
            formatPhone(phone) {
                if (!phone) return 'N/A';
                var cleaned = ('' + phone).replace(/\D/g, '');
                var match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/);
                if (match) {
                    return '(' + match[1] + ') ' + match[2] + '-' + match[3];
                }
                return phone;
            },
            
            getLocationName(locationId) {
                if (!locationId || !this.packet.locationsdict) return 'N/A';
                return this.packet.locationsdict[locationId] || 'N/A';
            },
            
            getStatusClass(order) {
                if (order.completed) return 'status-completed';
                if (order.dispatched_at) return 'status-dispatched';
                return 'status-pending';
            },
            
            getStatusText(order) {
                if (order.completed) return 'Completed';
                if (order.dispatched_at) return 'Dispatched';
                return 'Pending';
            },
            
            closeModal() {
                this.$bvModal.hide('order-details-modal');
            }
        },
        watch: {
            order: {
                immediate: true,
                handler(newVal) {
                    console.log('OrderDetails: order prop changed:', newVal);
                    if (newVal) {
                        this.loadOrderItems();
                    }
                }
            }
        },
        created: function() {
            console.log('OrderDetails created with order:', this.order);
            this.packet = JSON.parse(localStorage.getItem('packet')) || {};
        }
    };
</script>

<style scoped>
.order-details-container {
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, sans-serif;
}

.modern-order-card {
    background: white;
    border-radius: 10px;
    padding: 0;
}

.order-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    padding: 25px;
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    color: white;
    border-radius: 10px 10px 0 0;
}

.header-left {
    flex: 1;
}

.order-title {
    font-size: 1.8rem;
    font-weight: 700;
    margin-bottom: 10px;
}

.order-meta {
    display: flex;
    flex-wrap: wrap;
    gap: 15px;
    font-size: 0.9rem;
    opacity: 0.9;
}

.meta-item {
    display: flex;
    align-items: center;
}

.header-right {
    text-align: right;
}

.order-status {
    margin-bottom: 10px;
}

.status-badge {
    display: inline-block;
    padding: 4px 10px;
    border-radius: 12px;
    font-size: 0.8rem;
    font-weight: 600;
    text-transform: uppercase;
}

.status-badge.status-completed {
    background: rgba(72, 187, 120, 0.15);
    color: #276749;
    border: 1px solid rgba(72, 187, 120, 0.3);
}

.status-badge.status-dispatched {
    background: rgba(66, 153, 225, 0.15);
    color: #2c5282;
    border: 1px solid rgba(66, 153, 225, 0.3);
}

.status-badge.status-pending {
    background: rgba(237, 137, 54, 0.15);
    color: #c05621;
    border: 1px solid rgba(237, 137, 54, 0.3);
}

.payment-badge {
    padding: 4px 8px;
    border-radius: 4px;
    font-size: 0.8rem;
    font-weight: 600;
}

.payment-badge.badge-success {
    background: rgba(72, 187, 120, 0.15);
    color: #276749;
    border: 1px solid rgba(72, 187, 120, 0.3);
}

.payment-badge.badge-warning {
    background: rgba(237, 137, 54, 0.15);
    color: #c05621;
    border: 1px solid rgba(237, 137, 54, 0.3);
}

.order-total {
    background: rgba(255, 255, 255, 0.1);
    padding: 10px 15px;
    border-radius: 8px;
    border: 1px solid rgba(255, 255, 255, 0.2);
}

.total-label {
    font-size: 0.9rem;
    opacity: 0.8;
    margin-bottom: 5px;
}

.total-value {
    font-size: 1.5rem;
    font-weight: 700;
}

.customer-card,
.items-card,
.payment-card,
.notes-card {
    border: none;
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
    margin: 0 25px 20px;
}

.customer-info {
    margin-bottom: 15px;
}

.customer-info label {
    display: block;
    font-size: 0.8rem;
    color: #718096;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    margin-bottom: 5px;
    font-weight: 600;
}

.customer-info p {
    font-size: 1rem;
    color: #2d3748;
    font-weight: 500;
    margin: 0;
}

@media (max-width: 768px) {
    .order-header {
        flex-direction: column;
        gap: 15px;
        padding: 20px;
    }
    
    .header-right {
        text-align: left;
        width: 100%;
    }
    
    .customer-card,
    .items-card,
    .payment-card,
    .notes-card {
        margin: 0 15px 15px;
    }
}

@media (max-width: 480px) {
    .order-title {
        font-size: 1.5rem;
    }
    
    .order-meta {
        flex-direction: column;
        gap: 8px;
    }
    
    .total-value {
        font-size: 1.2rem;
    }
}
</style>


// WEBPACK FOOTER //
// src/components/Customers/OrderDetails.vue